import { useState, useEffect } from "react";
import { MediaUploadFileInfo } from "../upload/UploadMediaProps";
import FileRow, { FileRowProps } from "./fileRow/FileRow";
import BulkActions from "./bulk-actions/BulkActions";
import { useEventHandler } from "@kaltura/mediaspace-shared-hooks";

export interface FileRowsProps
    extends Pick<
        FileRowProps,
        | "editForm"
        | "editFormComponents"
        | "onCancel"
        | "onCanSaveForm"
        | "onReadyForPublish"
        | "onSubmitStarted"
        | "onSubmit"
        | "onRemove"
        | "bulkUpdatesCount"
        | "publishEnabled"
        | "formId"
        | "singleUpload"
        | "showEditFormButtons"
        | "showCancelButton"
        | "showMediaLink"
        | "saveButtonLabel"
        | "allowSaveUntouched"
        | "onBeforeSubmit"
    > {
    files: MediaUploadFileInfo[];
    entryIds: string[];
    onCancelAll: () => void;
    onEditAll: () => void;
    onPublishAll: () => void;
    bulkFilesIsEditable?: boolean;
    onEditFormShown?: (showingForm: boolean) => void;
}

/**
 * multiple file upload rows
 */
const FileRows = ({
    files,
    entryIds,
    onBeforeSubmit,
    onCancelAll,
    onEditAll,
    onPublishAll,
    publishEnabled,
    editFormComponents,
    bulkFilesIsEditable,
    singleUpload = false,
    showMediaLink,
    onEditFormShown,
    ...rest
}: FileRowsProps) => {
    const [fileOpenForEdit, setFileOpenForEdit] = useState<string | undefined>(
        files.length === 1 ? files[0].fileMetaData.tokenId : undefined
    );
    const [fileWantsToOpenForEdit, setFileWantsToOpenForEdit] = useState<string>();

    const handleEditFormShown = useEventHandler(onEditFormShown);

    useEffect(() => {
        handleEditFormShown?.(!!fileOpenForEdit);
    }, [fileOpenForEdit, handleEditFormShown]);

    return (
        <>
            {!singleUpload && (
                <BulkActions
                    files={files}
                    entryIds={entryIds}
                    onCancel={onCancelAll}
                    onEdit={onEditAll}
                    onPublish={onPublishAll}
                    publishEnabled={publishEnabled}
                    bulkFilesIsEditable={bulkFilesIsEditable}
                    showMyMediaLink={showMediaLink}
                />
            )}
            {files
                .concat()
                .reverse()
                .map((file) => {
                    const tokenId = file.fileMetaData.tokenId;
                    return (
                        <FileRow
                            {...rest}
                            key={`file_${tokenId}`}
                            fileInfo={file}
                            isOpen={fileOpenForEdit === tokenId || (singleUpload && !!tokenId)}
                            shouldClose={!!fileWantsToOpenForEdit}
                            onEdit={(tokenId: string) => {
                                if (!fileOpenForEdit) {
                                    setFileOpenForEdit(tokenId);
                                }
                                else {
                                    setFileWantsToOpenForEdit(tokenId);
                                }
                            }}
                            onClose={() => {
                                if (fileOpenForEdit === tokenId) {
                                    if (fileOpenForEdit === fileWantsToOpenForEdit) {
                                        // close all (specifically current) panels
                                        setFileOpenForEdit(undefined);
                                    }
                                    else {
                                        // open next panel
                                        setFileOpenForEdit(fileWantsToOpenForEdit);
                                    }
                                    // reset:
                                    setFileWantsToOpenForEdit(undefined);
                                }
                            }}
                            publishEnabled={publishEnabled}
                            editFormComponents={editFormComponents}
                            singleUpload={singleUpload}
                            onBeforeSubmit={onBeforeSubmit}
                            showMediaLink={showMediaLink}
                        />
                    );
                })}
        </>
    );
};

export default FileRows;
